import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import UserVerification from '../UserVerification';
import BrandBanner, { hasBrandBanner } from './shared/BrandBanner';
import PoweredBy from './shared/PoweredBy';
import SocialRow from './shared/SocialRow';
import PolicyLinks from './shared/PolicyLinks';
import ClassicResult from './shared/ClassicResult';
import { toAbsoluteUrl, getReadableAccent } from './trackingTemplateUtils';
import TrackAnotherButton from './shared/TrackAnotherButton';

// Template 1 — CLASSIC. The config-driven twin of the legacy page (Layout + TopBar + Order), which
// stays untouched and keeps serving every fallback brand. Self-contained like Modern/Minimal:
// serves live ("/", "/:id" via TemplateOrder) AND /preview, owns its chrome, and reuses the shared
// organs (UserVerification, Action/Timeline/Details via ClassicResult, SearchBar via children).

// The flat logo + "Powered by" bar — same look as the legacy TopBar, but driven by the brand PROP
// (the legacy TopBar reads the hardcoded domainDetails map, which config brands aren't in).
function LogoBar({ brand }) {
    const headerConfig = brand?.header;
    const webUrl = toAbsoluteUrl(brand?.webUrl);
    // Text-logo fallback color: explicit header text color wins; else the brand accent,
    // legibility-corrected for small text on the (default white) header (>=4.5:1).
    const nameColor = headerConfig?.color || getReadableAccent(brand?.bannerBgColor).accentStrong;
    return (
        <AppBar position="static" elevation={0} sx={{
            bgcolor: headerConfig?.bgcolor || '#fff',
            borderBottom: `1px solid ${headerConfig?.borderColor ?? 'rgba(0,0,0,0.08)'}`,
            color: headerConfig?.color || 'inherit',
            borderRadius: 0,
        }}>
            <Toolbar>
                {/* color+textDecoration guard the anchor case: a bare <a> paints UA blue over 'inherit' children */}
                <Box sx={{ flexGrow: 1, color: 'inherit', textDecoration: 'none' }}
                    {...(webUrl ? { component: 'a', href: webUrl, target: '_blank', rel: 'noreferrer' } : { component: 'div' })}>
                    {brand?.logo ? (
                        <Box component="img" src={brand.logo} alt={(brand && brand.name) || 'Brand logo'}
                            sx={{ height: { xs: 20, sm: 30 }, maxWidth: { xs: 160, sm: 260 }, objectFit: 'contain', m: '10px', display: 'block', cursor: webUrl ? 'pointer' : 'default', ...(brand?.style || {}) }} />
                    ) : brand?.name ? (
                        /* No logo uploaded: show the brand name (same fallback as Modern/Minimal). */
                        <Typography sx={{ fontWeight: 600, fontSize: 18, m: '10px', color: nameColor, display: 'inline-block', textDecoration: 'none' }}>
                            {brand.name}
                        </Typography>
                    ) : (
                        /* True default page (no brand at all): the Prozo mark. */
                        <Box component="img" src="/img/prozo.png" alt="Proship"
                            sx={{ height: { xs: 20, sm: 30 }, m: '10px', display: 'block' }} />
                    )}
                </Box>
                <PoweredBy logoHeight={{ xs: 15, sm: 25 }} labelSx={{ color: headerConfig?.color || 'text.secondary' }} />
            </Toolbar>
        </AppBar>
    );
}

export default function Classic({ brand, order, preview, children }) {
    const data = order || {};
    const hasOrder = !!data.createdDate;
    const showBanner = hasBrandBanner(brand);

    // No background here — the themed body gradient (CssBaseline) shows through, same as the live page.
    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header — brand banner if set, else the flat logo bar (mirrors the legacy Layout) */}
            {showBanner ? <BrandBanner brand={brand} /> : <LogoBar brand={brand} />}

            <Box sx={{ flexGrow: 1 }}>
                <Container sx={{ my: { xs: 2, sm: 3 }, mb: { xs: 4, sm: 8 } }}>
                    {/* OTP verification gate — same shared component as the legacy page (renders only for NDR) */}
                    <Box sx={{ mb: 2, '&:empty': { display: 'none' } }}><UserVerification /></Box>

                    {hasOrder ? (
                        <>
                            {/* Same "← Track another order" affordance as the legacy page (hidden in preview) */}
                            <Box sx={{ mb: 2 }}><TrackAnotherButton preview={preview} /></Box>
                            <Grid container spacing={2}>
                                {/* Stepper + status card + timeline + details — shared ClassicResult */}
                                <ClassicResult data={data} brand={brand} />
                            </Grid>
                        </>
                    ) : (
                        /* Center swaps by state: the search box / not-found screen passed as children —
                           header/footer stay constant (same behavior as Modern/Minimal). */
                        <Grid container spacing={2}>
                            <Grid item xs={12}>{children}</Grid>
                        </Grid>
                    )}
                </Container>
            </Box>

            {/* Brand footer — the merchant's configured footer note, social links and policy links.
                Same config fields Modern/Minimal render; without this the DEFAULT template would
                silently drop them (policy links especially are compliance-visible). */}
            {(brand?.footer || brand?.instaUrl || brand?.fbUrl || brand?.twitterUrl || brand?.linkedinUrl
                || brand?.whatsappNumber || brand?.termsAndConditions || brand?.returnPolicy || brand?.privacyPolicy) && (
                <Container sx={{ pb: 3 }}>
                    <Stack spacing={1} alignItems="center" sx={{ textAlign: 'center' }}>
                        {brand?.footer && (
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', maxWidth: 640 }}>
                                {brand.footer}
                            </Typography>
                        )}
                        <SocialRow brand={brand} />
                        <PolicyLinks brand={brand} />
                    </Stack>
                </Container>
            )}

            {/* Footer bar — when a banner is on top, the logo + "Powered by" bar sits at the bottom (mirrors legacy Layout) */}
            {showBanner && (
                <Box sx={{ mt: 'auto' }}><LogoBar brand={brand} /></Box>
            )}
        </Box>
    );
}
